import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminLayout from "../../components/layouts/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../hooks/useWebSocket";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ShoppingCart, Search, Clock } from "lucide-react";
import { cn, formatCurrency, formatDate, ORDER_STATUSES, PAYMENT_STATUSES } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManagerOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === "new_order") {
      setOrders((prev) => [message.order, ...prev]);
      toast.success(`New order: #${message.order.order_number}`);
    } else if (["order_status_update", "payment_verified", "payment_completed"].includes(message.type)) {
      setOrders((prev) =>
        prev.map((o) => (o.id === message.order.id ? message.order : o))
      );
    }
  }, []);

  useWebSocket(user?.restaurant_id, handleWebSocketMessage);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesFilter = filter === "all" || 
      (filter === "active" && ["new", "preparing", "ready"].includes(order.status)) ||
      (filter === "completed" && order.status === "completed");
    
    const matchesSearch = searchQuery === "" || 
      order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.table_number?.includes(searchQuery);
    
    return matchesFilter && matchesSearch;
  });

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="manager-orders">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Orders</h1>
              <p className="text-sm text-muted-foreground">
                {orders.length} total orders
              </p>
            </div>
          </div>
          
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-48"
              data-testid="order-search-input"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <Tabs value={filter} onValueChange={setFilter} className="w-full">
          <TabsList className="grid grid-cols-3 w-full max-w-sm">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ShoppingCart className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold text-lg mb-1">No orders found</h3>
              <p className="text-muted-foreground text-sm">Orders will appear here when placed</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredOrders.map((order) => {
              const statusInfo = ORDER_STATUSES[order.status];
              const paymentInfo = PAYMENT_STATUSES[order.payment_status];
              
              return (
                <Card key={order.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="font-mono font-bold text-lg">#{order.order_number.split("-")[1]}</p>
                          <p className="text-xs text-muted-foreground">
                            {order.order_type === "eat_in" ? `Table ${order.table_number || "-"}` : "Takeaway"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                          <p className="font-semibold">{formatCurrency(order.total)}</p>
                          <p className="text-xs text-muted-foreground">{order.items.length} items</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge className={cn(statusInfo?.color)}>
                          {statusInfo?.label}
                        </Badge>
                        <Badge className={cn(paymentInfo?.color)}>
                          {paymentInfo?.label}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Order Items */}
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex flex-wrap gap-2">
                        {order.items.map((item, idx) => (
                          <span key={idx} className="text-xs bg-muted px-2 py-1 rounded">
                            {item.quantity}x {item.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ManagerOrders;
